import { useEffect, useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import { useError } from '@zenbusiness/application-commons-logging';

export const ENTITY_SELECTOR_QUERY = gql`
  query BusinessEntitySelector($accountUuid: UUID!, $limit: Int!) {
    businessEntitySelector(accountUuid: $accountUuid, limit: $limit) {
      businessEntities {
        name
        uuid
        hasNameReservation
        domesticJurisdiction
      }
    }
  }
`;

export default function useBusinessEntitySelector(options = {}) {
  const { identified, accountUuid } = useIdentity();
  const { businessEntityUuid } = useBusinessEntity();
  const [businessEntities, setBusinessEntities] = useState([]);
  const { data, loading, error } = useQuery(ENTITY_SELECTOR_QUERY, {
    skip: !identified,
    noThrow: true,
    variables: {
      accountUuid,
      limit: options.limit || 500,
    },
  });

  const { businessEntitySelector } = data || {};
  const businessEntitiesData = loading || !businessEntitySelector ? null : businessEntitySelector?.businessEntities;

  const currentEntity = businessEntitiesData?.find?.(({ uuid }) => uuid === businessEntityUuid);

  useEffect(() => {
    if (loading || !businessEntitiesData) {
      return;
    }

    if (businessEntitiesData?.length !== businessEntities?.length) {
      setBusinessEntities(businessEntitiesData);
    }
  }, [loading, currentEntity, businessEntityUuid, businessEntitiesData, businessEntities?.length]);

  useError(error);

  return useMemoObject({
    error,
    loading,
    currentEntity,
    businessEntities,
  });
}
